import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../routes/routes";
import { resetPasswordAsync } from "../features/authentication/authenticationApi";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);

  const userId = searchParams.get("userId");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!userId || !token) {
      setError("Invalid password reset link. Please request a new one.");
    }
  }, [userId, token]);

  useEffect(() => {
    if (!isSuccess) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(ROUTES.LOGIN);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSuccess, navigate]);

  const handleSubmit = async () => {
    setPasswordError("");
    setError(null);

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (!userId || !token) {
      setError("Invalid password reset link.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPasswordAsync(userId, token, newPassword);
      setIsSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.[0]?.description ||
          "Failed to reset password. The link may have expired."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: `calc(100vh - var(--header-height, 72px))`,
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, sm: 5 }, width: "100%" }}
        >
          {isSuccess ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "rgba(34,197,94,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleIcon sx={{ width: 40, height: 40, color: "success.main" }} />
                </Box>
              </Box>
              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Password Reset Successfully!
              </Typography>
              <Typography variant="body1" align="center" sx={{ mb: 3, color: "text.secondary" }}>
                Your password has been changed. You can now log in with your new
                password.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to login in {countdown} seconds...
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={20} sx={{ color: "primary.main" }} />
              </Box>
            </>
          ) : error && (!userId || !token) ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "rgba(239,68,68,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ErrorIcon sx={{ width: 40, height: 40, color: "error.main" }} />
                </Box>
              </Box>
              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
              >
                Invalid Reset Link
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                >
                  Request a new reset link
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                component="h1"
                align="center"
                gutterBottom
                sx={{ mb: 1 }}
              >
                Set new password
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Enter your new password below.
              </Typography>

              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSubmitting}
                sx={{ mb: 2 }}
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ minWidth: 0, p: 1 }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                disabled={isSubmitting}
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{ py: 1.5, mb: 2 }}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate(ROUTES.LOGIN)}
                >
                  Back to login
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
